import { AxiosError } from "axios";
import { AppError, FieldErrors } from "./AppError";

type ApiErrorPayload = {
  detail?: unknown;
  message?: unknown;
  error?: unknown;
  [key: string]: unknown;
};

type ValidationItem = {
  loc?: unknown;
  msg?: unknown;
  message?: unknown;
};

const SERVER_ERROR_MESSAGE = "Server error. Please try again later.";
const NETWORK_ERROR_MESSAGE = "Network error. Please check your connection and try again.";
// 502 от бэкенда с этим кодом — сбой Plaid, а не наш: message — текст Plaid для бухгалтера,
// details.error_code — код Plaid (docs/plaid-api-contract.md). Его не прячем за SERVER_ERROR_MESSAGE.
export const PLAID_ERROR_CODE = "plaid_error";

function isPlaidError(payload: ApiErrorPayload | undefined): boolean {
  const nested = payload?.error;
  return Boolean(
    nested && typeof nested === "object" && (nested as ApiErrorPayload).code === PLAID_ERROR_CODE
  );
}

function cleanRawMessage(value: string): string {
  const match = value.match(/string='([^']+)'/);
  return match?.[1] || value;
}

function getValidationField(loc: unknown): string | undefined {
  if (!Array.isArray(loc)) return undefined;

  const field = [...loc].reverse().find((part) => typeof part === "string" && part !== "body");
  return typeof field === "string" ? field : undefined;
}

function parseValidationItems(items: unknown[]): { messages: string[]; fieldErrors: FieldErrors } {
  const messages: string[] = [];
  const fieldErrors: FieldErrors = {};

  items.forEach((item) => {
    if (!item || typeof item !== "object") return;

    const validationItem = item as ValidationItem;
    const rawMessage = validationItem.msg || validationItem.message;
    if (typeof rawMessage !== "string" || !rawMessage) return;

    const field = getValidationField(validationItem.loc);
    const message = field ? `${field}: ${rawMessage}` : rawMessage;

    messages.push(message);
    if (field && !fieldErrors[field]) {
      fieldErrors[field] = rawMessage;
    }
  });

  return { messages, fieldErrors };
}

function parseObjectFieldErrors(data: ApiErrorPayload): { messages: string[]; fieldErrors: FieldErrors } {
  const messages: string[] = [];
  const fieldErrors: FieldErrors = {};

  Object.entries(data)
    .filter(([key]) => !["detail", "message", "error"].includes(key))
    .forEach(([key, value]) => {
      if (Array.isArray(value)) {
        const message = value.filter((item) => typeof item === "string").join(", ");
        if (message) {
          fieldErrors[key] = message;
          messages.push(`${key}: ${message}`);
        }
        return;
      }

      if (typeof value === "string" && value) {
        fieldErrors[key] = value;
        messages.push(`${key}: ${value}`);
      }
    });

  return { messages, fieldErrors };
}

function buildAppError(
  message: string,
  statusCode?: number,
  code?: string,
  details?: unknown,
  fieldErrors?: FieldErrors
): AppError {
  return new AppError(message, code, statusCode, { details, fieldErrors });
}

function parseNestedError(
  value: unknown,
  statusCode?: number
): AppError | null {
  if (typeof value === "string" && value) {
    return buildAppError(cleanRawMessage(value), statusCode);
  }

  if (Array.isArray(value)) {
    const { messages, fieldErrors } = parseValidationItems(value);
    if (messages.length > 0) {
      return buildAppError(messages.join("; "), statusCode, undefined, value, fieldErrors);
    }
  }

  if (value && typeof value === "object") {
    const nested = value as ApiErrorPayload;
    const code = typeof nested.code === "string" ? nested.code : undefined;

    if (typeof nested.message === "string" && nested.message) {
      return buildAppError(nested.message, statusCode, code, nested.details || nested);
    }

    if (typeof nested.detail === "string" && nested.detail) {
      return buildAppError(cleanRawMessage(nested.detail), statusCode, code, nested.details || nested);
    }

    if (Array.isArray(nested.detail)) {
      const { messages, fieldErrors } = parseValidationItems(nested.detail);
      if (messages.length > 0) {
        return buildAppError(messages.join("; "), statusCode, code, nested.detail, fieldErrors);
      }
    }

    const { messages, fieldErrors } = parseObjectFieldErrors(nested);
    if (messages.length > 0) {
      return buildAppError(messages.join("; "), statusCode, code, nested, fieldErrors);
    }
  }

  return null;
}

export function parseApiError(error: AxiosError<unknown>): AppError {
  if (process.env.NODE_ENV === "development") {
    console.log("Raw API error data:", error.response?.data);
  }

  const data = error.response?.data;
  const statusCode = error.response?.status;
  const payload = data && typeof data === "object" && !Array.isArray(data)
    ? (data as ApiErrorPayload)
    : undefined;

  if (!error.response) {
    return buildAppError(NETWORK_ERROR_MESSAGE, statusCode);
  }

  if (statusCode && statusCode >= 500) {
    if (isPlaidError(payload)) {
      const plaidError = parseNestedError(payload?.error, statusCode);
      if (plaidError) return plaidError;
    }
    return buildAppError(SERVER_ERROR_MESSAGE, statusCode);
  }

  if (!data) {
    return buildAppError(error.message || "Unknown API error", statusCode);
  }

  if (typeof data === "string") {
    return buildAppError(cleanRawMessage(data), statusCode);
  }

  if (Array.isArray(data)) {
    const { messages, fieldErrors } = parseValidationItems(data);
    if (messages.length > 0) {
      return buildAppError(messages.join("; "), statusCode, undefined, data, fieldErrors);
    }
  }

  const nestedError = parseNestedError(payload?.error, statusCode);
  if (nestedError) {
    return nestedError;
  }

  if (typeof payload?.detail === "string" && payload.detail) {
    return buildAppError(cleanRawMessage(payload.detail), statusCode);
  }

  const detail = payload?.detail;
  if (Array.isArray(detail)) {
    const { messages, fieldErrors } = parseValidationItems(detail);
    if (messages.length > 0) {
      return buildAppError(messages.join("; "), statusCode, undefined, detail, fieldErrors);
    }
  }

  if (typeof payload?.message === "string" && payload.message) {
    return buildAppError(payload.message, statusCode);
  }

  if (payload) {
    const { messages, fieldErrors } = parseObjectFieldErrors(payload);
    if (messages.length > 0) {
      return buildAppError(messages.join("; "), statusCode, undefined, payload, fieldErrors);
    }
  }

  return buildAppError(error.message || "Unknown API error", statusCode);
}
